class IaCollectionFiltersDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawerInstanceId = this.dataset.drawerId;
    this.isOpen = this.classList.contains('is-open');
    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.windowEl = this.querySelector('[data-drawer-window]');
    this._onTriggerClick = this.toggleDrawer.bind(this);
    this._onKeyup = (e) => {
      if (!this.isOpen) return;
      if (e.key === 'Escape') this.close();
    };
    this._onOverlayClick = this.close.bind(this);
    this._onDrawerTransitionEnd = this._handleDrawerTransitionEnd.bind(this);
    this._onAccordionToggle = () => queueMicrotask(() => this._syncCollapsedAccordions());
    this._accordionLayoutTimer = null;
  }

  connectedCallback() {
    if (this._iaDrawerListenersBound) return;
    this._iaDrawerListenersBound = true;
    document
      .querySelectorAll(`[data-drawer-trigger="${this.drawerInstanceId}"]`)
      .forEach((btn) => btn.addEventListener('click', this._onTriggerClick));
    document.addEventListener('keyup', this._onKeyup);
    this.overlay?.addEventListener('click', this._onOverlayClick);
    this.addEventListener('accordion:toggle', this._onAccordionToggle);
    if (this.parentNode !== document.body) {
      document.body.appendChild(this);
    }
  }

  disconnectedCallback() {
    if (this.isOpen) this.close();
    if (!this._iaDrawerListenersBound) return;
    this._iaDrawerListenersBound = false;
    document
      .querySelectorAll(`[data-drawer-trigger="${this.drawerInstanceId}"]`)
      .forEach((btn) => btn.removeEventListener('click', this._onTriggerClick));
    document.removeEventListener('keyup', this._onKeyup);
    this.overlay?.removeEventListener('click', this._onOverlayClick);
    this.removeEventListener('accordion:toggle', this._onAccordionToggle);
    this._clearAccordionLayoutScheduling();
  }

  _syncCollapsedAccordions() {
    this.querySelectorAll('accordion-item').forEach((item) => {
      const content = item.querySelector('[data-accordion-content]');
      if (content) content.inert = !item.hasAttribute('open');
    });
  }

  _focusWindow(attempt = 0) {
    if (!this.windowEl || !this.classList.contains('is-open')) return;
    const userMovedIn = attempt > 0 && this.contains(document.activeElement);
    if (!userMovedIn) this.windowEl.focus({ preventScroll: true });
    if (!this.contains(document.activeElement)) {
      if (attempt < 80) setTimeout(() => this._focusWindow(attempt + 1), 25);
      return;
    }
    if (typeof trapFocus === 'function') {
      trapFocus(this.windowEl, document.activeElement);
      if (typeof trapFocusHandlers !== 'undefined' && trapFocusHandlers.keydown) {
        document.addEventListener('keydown', trapFocusHandlers.keydown);
      }
    }
  }

  _announceOpened() {
    const status = this.querySelector('[data-ia-filter-results-status]');
    const message = this.windowEl?.dataset.openedMessage;
    if (!status || !message) return;
    status.textContent = '';
    clearTimeout(this._openedMessageTimer);
    this._openedMessageTimer = setTimeout(() => {
      if (this.classList.contains('is-open')) status.textContent = message;
    }, 600);
  }

  _getReturnFocusTarget() {
    const saved = this._returnFocusTo;
    if (saved?.isConnected && !this.contains(saved) && saved.offsetParent !== null) return saved;
    return [...document.querySelectorAll(`[data-drawer-trigger="${this.drawerInstanceId}"]`)].find(
      (btn) => !this.contains(btn) && btn.offsetParent !== null,
    );
  }

  _clearAccordionLayoutScheduling() {
    if (this._accordionLayoutTimer != null) {
      clearTimeout(this._accordionLayoutTimer);
      this._accordionLayoutTimer = null;
    }
    this.windowEl?.removeEventListener(
      'transitionend',
      this._onDrawerTransitionEnd,
    );
    this._accordionLayoutPending = false;
  }

  _refreshOpenAccordionHeights() {
    if (!this.classList.contains('is-open')) return;
    this.querySelectorAll('accordion-item[open]').forEach((item) => {
      if (typeof item._updateState === 'function') {
        item._updateState();
      }
    });
  }

  _handleDrawerTransitionEnd(event) {
    if (event.target !== this.windowEl) return;
    if (event.propertyName !== 'transform' && event.propertyName !== 'visibility') {
      return;
    }
    this._finishAccordionLayoutAfterOpen();
  }

  _finishAccordionLayoutAfterOpen() {
    if (this._accordionLayoutPending !== true) return;
    this._accordionLayoutPending = false;
    this._clearAccordionLayoutScheduling();
    this._refreshOpenAccordionHeights();
  }

  toggleDrawer(event) {
    event.preventDefault();
    this.isOpen = !this.isOpen;
    this.isOpen ? this.open() : this.close();
  }

  close() {
    const wasOpen = this.classList.contains('is-open');
    this._clearAccordionLayoutScheduling();
    this.windowEl?.style.removeProperty('visibility');
    this.classList.remove('is-open');
    this.isOpen = false;
    document.body.classList.remove('overflow-hidden');
    window.iaTheme?.showExtraStickyElements?.();
    document
      .querySelectorAll(`[data-drawer-trigger="${this.drawerInstanceId}"]`)
      .forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    clearTimeout(this._openedMessageTimer);
    const status = this.querySelector('[data-ia-filter-results-status]');
    if (status) status.textContent = '';
    if (wasOpen) {
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      const focusInside = this.contains(document.activeElement) || document.activeElement === document.body;
      if (focusInside) this._getReturnFocusTarget()?.focus({ preventScroll: true });
      this._returnFocusTo = null;
    }
    const fid = this.dataset.drawerSyncFormId;
    if (fid) {
      document.dispatchEvent(
        new CustomEvent('ia-collection-filters-drawer-closed', {
          detail: { formId: fid },
        }),
      );
    }
  }

  open() {
    const opener = document.activeElement;
    if (opener && opener !== document.body && !this.contains(opener)) this._returnFocusTo = opener;
    this._clearAccordionLayoutScheduling();
    this.classList.add('is-open');
    this.isOpen = true;
    if (
      document.documentElement.scrollHeight >
      document.documentElement.clientHeight
    ) {
      document.body.classList.add('overflow-hidden');
    }
    window.iaTheme?.hideExtraStickyElements?.();
    document
      .querySelectorAll(`[data-drawer-trigger="${this.drawerInstanceId}"]`)
      .forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
    this.dispatchEvent(
      new CustomEvent('ia-drawer-opened', {
        detail: { drawerId: this.drawerInstanceId },
      }),
    );

    this._syncCollapsedAccordions();
    if (this.windowEl) this.windowEl.style.visibility = 'visible';
    this._focusWindow();
    this._announceOpened();

    this._accordionLayoutPending = true;
    this.windowEl?.addEventListener('transitionend', this._onDrawerTransitionEnd);
    this._accordionLayoutTimer = window.setTimeout(() => {
      this._finishAccordionLayoutAfterOpen();
    }, 400);
  }
}

if (!customElements.get('ia-drawer-component')) {
  customElements.define('ia-drawer-component', IaCollectionFiltersDrawer);
}
